import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useMatches } from 'react-router';
import { type RequestContext, type SerializeFrom } from '@cx-web/domain-types';
import { getRepubMetaFromRepub } from '@cx-web/utils';
import { getDefaultProduct } from '@cx-web/utils/getDefaultProduct';
import { captureException } from '@sentry/react-router';

import BettingInsights from '~/components/BettingInsights';
import SortingLayout from '~/components/BettingInsights/components/SortingLayout';
import { Error as ErrorComponent } from '~/components/error/Error.tsx';
import { Hero } from '~/components/hero/Hero.tsx';
import SearchWrapper from '~/components/SearchWrapper';
import { type PositiveEvIndex } from '~/configuration/types';
import { createPositiveEvConfig } from '~/pages/config';
import { positiveEvIndex } from '~/search-domain/indexes';

import type { loader as rootLoader } from '../../../root-loader';

import { loader } from './loader';
import { meta } from './meta';

export default function PositiveEvRoute() {
  const matches = useMatches();
  const rootMatch = matches.find(match => /oddschecker-plus\/root$/.test(match.id));
  const rootLoaderData = rootMatch?.data as SerializeFrom<typeof rootLoader>;
  if (!rootLoaderData) {
    throw new Error('Root loader data is missing');
  }
  const { t } = useTranslation();

  const { geolocationInfo, siteConfig } = rootLoaderData;
  const {
    availableProducts,
    currentPath,
    monthlyProducts,
    repub,
    requestContext,
    resourcesUrl,
    subscriptionStatus,
    yearlyProducts,
  } = useLoaderData<typeof loader>();
  const { repubIs, repubPath } = getRepubMetaFromRepub(repub);
  const config = createPositiveEvConfig(requestContext as RequestContext, { geolocationInfo });

  const userHasCancelledSubscription = requestContext.user.subscriptions.some(
    ({ status }: { status: string }) => status === 'CANCELLED',
  );

  const userHasActiveSubscription = requestContext.user.subscriptions.some(
    ({ status }: { status: string }) => status === 'ACTIVE' || status === 'TRIAL',
  );

  const defaultMonthlyProduct = getDefaultProduct(monthlyProducts);
  const defaultYearlyProduct = getDefaultProduct(yearlyProducts);

  return (
    <SortingLayout>
      <Hero
        currentPath={currentPath}
        hasActiveSubscription={userHasActiveSubscription}
        hasCancelledSubscription={userHasCancelledSubscription}
        monthlyProduct={defaultMonthlyProduct}
        repub={repub}
        repubPath={repubPath}
        shouldHideDetailsMobile={true}
        yearlyProduct={defaultYearlyProduct}
      >
        <>
          <h1>{t('CX_PLUS_EV_TITLE')}</h1>
          <h2>{t('CX_PLUS_EV_HERO_TITLE')}</h2>

          <h3>
            {t('CX_PLUS_EV_SUB')}
            <b>{repubIs.GZ ? t('CX_PLUS_EV_SUBTITLE') : t('CX_PLUS_EV_TITLE')}</b>
            {t('CX_PLUS_EV_END')}?
          </h3>
          <p>{t('CX_PLUS_EV_HERO_HELPER_TEXT')}</p>
        </>
      </Hero>
      <ErrorBoundary
        fallbackRender={({ error }) => {
          captureException(error);
          return <ErrorComponent indexName={positiveEvIndex} message={error.message} title="Something went wrong" />;
        }}
      >
        <SearchWrapper
          config={config}
          indexName={positiveEvIndex}
          repub={repub}
          state={geolocationInfo.subdivisionCode}
          userTZ={requestContext.user.TZ}
        >
          <BettingInsights<PositiveEvIndex>
            availableProducts={availableProducts}
            bookmakers={siteConfig.bookmakers}
            config={config}
            currentPath={currentPath}
            indexName={positiveEvIndex}
            redirectPath={currentPath ?? undefined}
            resourcesUrl={resourcesUrl}
            subscriptionStatus={subscriptionStatus}
            userHasCancelledSubscription={userHasCancelledSubscription}
            wallType={config.wallType}
          />
        </SearchWrapper>
      </ErrorBoundary>
    </SortingLayout>
  );
}

export { loader, meta };
